import React, { useEffect, useMemo, useRef, useState } from "react";
import { MoreHorizontal, Menu } from "@/components/icons";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/Badges";
import { NotificationBell } from "@/components/NotificationBell";
import { SiteStrip } from "@/components/SiteStrip";
import { useShell } from "@/components/ShellContext";
import { useBalkContext } from "@/lib/balkState";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// DE KLOK — the only element in the product that changes without a user action,
// once a minute, only while a P1 is open. A number that moves is the strongest
// thing an interface owns, so it is spent on the one fact that costs money.
export const DowntimeClock = ({ since }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);
  if (!since) return null;
  const mins = Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 60000));
  const text = mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)} u ${mins % 60} min`;
  return <span className="font-mono tabular-nums" data-testid="downtime-clock">{text}</span>;
};

const Action = ({ action, collapsed }) => {
  const Icon = action.icon;
  const primary = action.variant === "primary";
  return (
    <button
      type="button"
      data-testid={action.testid || `console-action-${action.key}`}
      onClick={action.onClick}
      className={`hidden items-center gap-2 whitespace-nowrap rounded px-4 text-body font-medium transition-colors sm:inline-flex ${
        collapsed ? "h-8" : "h-9"
      } ${
        primary
          ? "bg-white text-console hover:bg-mist"
          : "border border-console-lijn text-white hover:bg-console-diep"
      }`}
    >
      {Icon && <Icon size={18} />}
      {action.label}
    </button>
  );
};

const OverflowMenu = ({ items, className, testid }) => {
  if (!items.length) return null;
  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Meer acties"
            data-testid={testid}
            className="flex h-10 w-10 items-center justify-center rounded text-white hover:bg-console-diep sm:h-8 sm:w-8"
          >
            <MoreHorizontal size={20} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {items.map((a) => (
            <DropdownMenuItem
              key={a.key}
              onClick={a.onClick}
              data-testid={`console-sheet-${a.key}`}
              className="h-12 text-body sm:h-10"
            >
              {a.icon && <a.icon size={18} className="mr-2" />}
              {a.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

/**
 * PageConsole — the navy band welded to the top of every screen. It replaces the
 * old 56px topbar and the thirteen different <h1> blocks. This file is the only
 * place in the app that may contain an <h1>.
 */
export const PageConsole = ({
  variant = "view", title, facts = [], actions = [], code, srTitle, srTestid, backSlot, children,
  testid = "page-console",
}) => {
  const { user, hasRole } = useAuth();
  const shell = useShell();
  const balk = useBalkContext();
  const sentinel = useRef(null);
  const headerRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setCollapsed(!e.isIntersecting), { threshold: 1 });
    io.observe(node);
    return () => io.disconnect();
  }, []);

  // Park the sticky table head exactly under the console. A ResizeObserver, not
  // an effect on state: reading offsetHeight while the padding transition is
  // still running captures a stale height and leaves a gap that rows peek
  // through.
  useEffect(() => {
    const node = headerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const set = () => document.documentElement.style.setProperty("--console-h", `${Math.round(node.offsetHeight)}px`);
    const ro = new ResizeObserver(set);
    ro.observe(node);
    set();
    return () => ro.disconnect();
  }, []);

  // The primary action is chosen by VARIANT, with position only as a fallback.
  // Two mechanisms (desktop styling by variant, mobile bottom bar by position)
  // could disagree, and on LocationDetail they did: the phone bar offered a PDF
  // download where the desktop offered "Bewerken".
  const primary = useMemo(() => actions.find((a) => a.variant === "primary") || actions[0], [actions]);
  const rest = useMemo(() => actions.filter((a) => a !== primary), [actions, primary]);
  const desktopVisible = useMemo(() => actions.slice(0, 2), [actions]);
  const desktopOverflow = useMemo(() => actions.slice(2), [actions]);
  const steel = variant === "object";

  return (
    <>
      <div ref={sentinel} aria-hidden="true" className="h-px w-full" />
      <header
        ref={headerRef}
        data-testid={testid}
        data-collapsed={collapsed ? "true" : "false"}
        data-variant={variant}
        className={`op-donker sticky top-0 z-40 w-full rounded-none ${steel ? "bg-plaat" : "bg-console"}`}
      >
        <div
          className={`transition-[padding] duration-150 ease-linear px-4 sm:px-6 ${
            collapsed ? "pb-2 pt-2" : "pb-3 pt-3 sm:pb-3 sm:pt-4"
          }`}
        >
          <div className="flex items-center gap-2">
            {shell && (
              <button
                type="button"
                onClick={shell.openNav}
                aria-label="Navigatie openen"
                data-testid="mobile-menu-button"
                className="-ml-2 flex h-10 w-10 shrink-0 items-center justify-center rounded text-white hover:bg-console-diep lg:hidden"
              >
                <Menu size={20} />
              </button>
            )}

            {backSlot}

            <div className="min-w-0 flex-1">
              {steel ? (
                <h1 className="truncate font-mono text-display text-white" data-testid="console-code">
                  {code}
                  {srTitle && <span className="sr-only" data-testid={srTestid}> {srTitle}</span>}
                </h1>
              ) : (
                <h1
                  className={`truncate text-white ${collapsed ? "text-kop" : "text-titel"}`}
                  data-testid={srTestid || "console-title"}
                >
                  {title}
                </h1>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {desktopVisible.map((a) => <Action key={a.key} action={a} collapsed={collapsed} />)}
              {/* Desktop: only what did not fit. Phone: everything except the bottom bar. */}
              <OverflowMenu items={desktopOverflow} className="hidden sm:block" testid="console-more" />
              <OverflowMenu items={rest} className="sm:hidden" testid="console-more-mobile" />

              <div className="ml-1 flex items-center gap-3 sm:ml-2 sm:border-l sm:border-console-lijn sm:pl-4">
                {user && hasRole("owner", "td") && <NotificationBell />}
                {/* No user, no user chrome: the public QR page showed an empty
                    grey pill and a blank name to a man with wet gloves. */}
                {user && (
                  <>
                    <span className="hidden max-w-[9rem] truncate text-body text-mist sm:inline" data-testid="console-user">
                      {user.name}
                    </span>
                    <span className="hidden sm:inline"><RoleBadge role={user.role} /></span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Facts — an array joined with a middot, never a sentence. They wrap
              rather than truncate: half a clipped word is worse than a second line,
              and on a phone we show the first three, because that is what fits. */}
          {!collapsed && facts.length > 0 && (
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 text-data text-mist sm:text-body" data-testid="console-facts">
              {/* Three facts, no more, at any width — and the middot travels WITH
                  its fact, so a wrap never leaves a dangling separator. */}
              {facts.slice(0, 3).map((f, i) => (
                <span key={i}>{i > 0 ? "· " : ""}{f}</span>
              ))}
            </div>
          )}

          {/* Children may be a function: the plate needs to know whether the
              band is collapsed so it can drop its field grid instead of pinning
              342px of header to a 664px phone screen. */}
          {typeof children === "function" ? children(collapsed) : children}
        </div>
        {/* No BalkProvider ⇒ no strip. Outside AppShell it could only ever render
            nineteen hardcoded resting segments announcing "alles in bedrijf" — on
            the one page whose purpose is reporting that a machine is broken. */}
        {balk && <SiteStrip />}
      </header>

      {primary && (
        <button
          type="button"
          data-testid={`bottombar-${primary.key}`}
          onClick={primary.onClick}
          className="fixed inset-x-0 bottom-0 z-40 flex h-14 w-full items-center justify-center gap-2 bg-console text-body font-medium text-white sm:hidden"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          {primary.icon && <primary.icon size={20} />}
          {primary.label}
        </button>
      )}
    </>
  );
};

/** Page — console plus body, so the gutter, the sticky offset and the bottom-bar
 *  reservation cannot drift apart. */
export const Page = ({ console: consoleProps = {}, children }) => (
  <div className="page">
    <PageConsole {...consoleProps} />
    <div className="page-body" data-bottombar={(consoleProps.actions || []).length > 0 ? "true" : "false"}>
      {children}
    </div>
  </div>
);
